// Le modèle des étapes. Forme constante : le rail les affiche toutes, tous les
// jours ; seul le grisé change (P3). Tu mémorises la carte, et un remplaçant
// voit l'étendue réelle du poste.
//
// Douze depuis le 13/08/2026 : l'apport SCO (02A) s'est glissé avant les apports
// caissiers. Les CODES sont l'identité et ne bougent jamais — seul le rang, qui
// n'est qu'un ordre d'affichage, a été renuméroté.
//
// Ce fichier doit rester en phase avec public.fc_etapes_modele() côté SQL —
// mêmes codes, mêmes libellés, même ordre. Un test le vérifie.

import type { CodeBranche } from './types'

export type ModuleHw = 'apports' | 'prelev' | 'paiements' | 'kadeos' | 'tickets' | 'inventaire'

export const LIBELLES_HW: Record<ModuleHw, string> = {
  apports:    'APPORTS',
  prelev:     'PRÉLÈVEMENTS J',
  paiements:  'PAIEMENTS À SCANNER',
  kadeos:     'BONS KADÉOS',
  tickets:    'TICKETS',
  inventaire: 'INVENTAIRE COFFRE',
}

/** Nature de l'étape — la 11 restitue là où les dix premières collectent. */
export type Nature = 'collecte' | 'aiguillage' | 'revue' | 'restitution'

export interface Question {
  texte: string
  /** Pourquoi la machine ne peut pas répondre à ta place (P5). */
  pourquoi: string
}

export interface Branche {
  id: CodeBranche
  nom: string
  desc: string
  hw?: ModuleHw[]
  question?: Question
  /** Ce que la branche reprend ou apporte — affiché dans son écran. */
  reprise?: string
  /** Contenu à spécifier — bandeau « en construction » (§7.6). */
  chantier?: string
}

export interface ModeleEtape {
  code: string
  nom: string
  sous: string
  nature: Nature
  hw?: ModuleHw[]
  question?: Question
  branches?: Branche[]
  /** Ce qui est repris tel quel de la V2 — affiché dans l'écran de l'étape. */
  reprise?: string
}

export const ETAPES: ModeleEtape[] = [
  {
    code: '01', nom: 'Import', sous: 'États ORIKA', nature: 'collecte',
    reprise: 'Avertissements V2 conservés — paiements sans badge, canary des tickets annulés.',
  },
  {
    // AVANT les apports caissiers, et pas après : ce qu'on décide de charger
    // dans les monnayeurs doit être arrêté avant d'ouvrir les apports, pour
    // figurer avec eux sur le papier qu'on imprime à l'étape 02.
    code: '02A', nom: 'Apport SCO', sous: 'Recharge des monnayeurs', nature: 'restitution',
    reprise: 'Nouveauté : le ticket « Vérification du tiroir » de la nuit, déposé par l’extension '
           + 'ORIKA, dit ce qu’il faut recharger dans chaque caisse automatique. La proposition '
           + 'est modifiable, et ce qui est retenu s’inscrit dans les apports du jour.',
  },
  {
    code: '02', nom: 'Apports', sous: 'Monnaie caissiers', nature: 'collecte',
    question: {
      texte: 'Des apports ont-ils été faits aujourd’hui ?',
      pourquoi: 'Aucune donnée ORIKA ne permet de le déduire — c’est une action physique.',
    },
    reprise: 'Fonctionnel V2 intégral : rouleaux (8 dénominations) + billets 5 et 10 €, '
           + 'valorisation bancaire, fonds de caisse fixes, RPC save_apport atomique.',
  },
  {
    code: '03', nom: 'PAD', sous: 'Paiements à distance', nature: 'collecte',
    reprise: 'Logique nouvelle : quatre issues, deux natures. Le montant réel fait foi.',
  },
  {
    code: '04', nom: 'Prélèvements', sous: 'Caisses traditionnelles', nature: 'collecte',
    hw: ['prelev'],
    reprise: 'Fonctionnel V2 intégral : enveloppes de 500 €, concordance coffre / papier, '
           + 'auto-remplissage depuis le dernier inventaire validé, cumul hebdomadaire mardi → lundi.',
  },
  {
    code: '05A', nom: 'Rapport opérateurs', sous: 'Ce que la journée dit de chacun', nature: 'revue',
    reprise: 'Étape d’INFORMATION : rien ne s’y saisit, la validation vaut accusé de lecture. '
           + 'Elle réunit le rapport opérateur — qu’on ne voyait qu’en marge du dépouillement — '
           + 'et les opérations à risque de l’ancienne étape 09, dont elle reprend le statut.',
  },
  {
    code: '05B', nom: 'Dépouillement', sous: 'Opérateurs · chèques, bons, TR/CAP',
    nature: 'collecte', hw: ['paiements', 'kadeos'],
  },
  {
    code: '06', nom: 'Prélèvements SCO', sous: 'Caisses automatiques', nature: 'collecte',
    question: {
      texte: 'Des prélèvements SCO ont-ils été faits aujourd’hui ?',
      pourquoi: 'Aucune donnée ORIKA ne permet de le déduire — c’est une action physique.',
    },
    reprise: 'Comptage du vidage des automates : pièces (un total), billets en vrac par '
           + 'coupure, enveloppes scellées de 500 €. Seules les enveloppes traversent vers le '
           + 'coffre — les pièces et le vrac y sont déjà par le comptage physique.',
  },
  {
    code: '07', nom: 'Retours', sous: 'Contrôle des tickets', nature: 'collecte', hw: ['tickets'],
    reprise: 'Fonctionnel V2 conservé : scan HID, statuts (validé = null), recherche, '
           + 'liste des manquants, détection de doublons, RPC set_ticket_status.',
  },
  {
    code: '08', nom: 'Formation', sous: 'Mode formation', nature: 'collecte',
    reprise: 'Ajout V5 : un motif libre par ticket, saisi au clavier, obligatoire pour valider '
           + 'le ticket (point ouvert O9, révisable).',
  },
  {
    code: '09', nom: 'Tickets annulés', sous: 'Contrôle', nature: 'collecte', hw: ['tickets'],
    reprise: 'Le filtre anomalies et le canary du parseur restent en place tant que l’incident '
           + 'connu (mapping montant par article vs par ticket) n’est pas corrigé.',
  },
  {
    code: '10', nom: 'Coffre', sous: 'Inventaire et prélèvement', nature: 'collecte', hw: ['inventaire'],
    reprise: 'Fonctionnel V2 intégral : 8 sections, computeTotals partagé PC-mobile, '
           + 'validation avec recalcul serveur, motif d’écart obligatoire, quota des 20 inventaires.',
  },
  {
    code: '11', nom: 'Saisie COL MNC & Documents', sous: 'Restitution', nature: 'restitution',
    reprise: 'Les deux moteurs PDF de la V2 sont conservés en l’état. Le format ORIKA de '
           + '/api/generate-pdf ne doit pas être modifié.',
  },
]

export const ETAPE_PAR_CODE = new Map(ETAPES.map((e) => [e.code, e]))

export const CODES = ETAPES.map((e) => e.code)

/** Le verrou de run expire seul après 15 minutes sans activité (§10). */
export const VERROU_TTL_MS = 15 * 60 * 1000
/** Battement de cœur : prolonge le verrou et alimente le chrono. */
export const HEARTBEAT_MS = 60 * 1000
/** En dessous de 10 minutes d'absence, la reprise serait du bruit (§9.1). */
export const REPRISE_SEUIL_MS = 10 * 60 * 1000
